import { supabase } from '../lib/supabase';

export const subscriptionService = {
  /**
   * Check if user has premium features access
   */
  async hasPremiumAccess(userId = null) {
    try {
      const { data, error } = await supabase?.rpc('has_premium_features', {
        user_uuid: userId
      });
      
      if (error) throw error;
      return data === true;
    } catch (error) {
      console.error('Error checking premium access:', error);
      return false;
    }
  },

  /**
   * Check if user has specific subscription feature
   */
  async hasFeatureAccess(featureName) {
    try {
      const { data, error } = await supabase?.rpc('has_subscription_feature', {
        feature_name: featureName
      });
      
      if (error) throw error;
      return data === true;
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  },

  /**
   * Get current user's subscription status
   */
  async getCurrentSubscription() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile, error } = await supabase?.from('user_profiles')?.select('subscription_tier, subscription_status, trial_end_date, trial_start_date')?.eq('id', user?.id)?.single();

      if (error) throw error;
      
      return {
        tier: profile?.subscription_tier,
        status: profile?.subscription_status,
        trialEndDate: profile?.trial_end_date,
        trialStartDate: profile?.trial_start_date,
        isTrialActive: profile?.subscription_status === 'trial' && new Date() < new Date(profile.trial_end_date)
      };
    } catch (error) {
      console.error('Error getting subscription:', error);
      return null;
    }
  },

  /**
   * Get subscription pricing information
   */
  async getSubscriptionPricing() {
    try {
      const { data, error } = await supabase?.rpc('get_subscription_pricing');
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting subscription pricing:', error);
      return [];
    }
  },

  /**
   * Get detailed subscription with billing info
   */
  async getDetailedSubscription(userId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) throw new Error('User not authenticated');

      const { data: subscription, error } = await supabase?.from('subscriptions')?.select(`
          *,
          billing_history (
            id,
            amount,
            status,
            created_at,
            billing_period_start,
            billing_period_end
          )
        `)?.eq('user_id', targetUserId)?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return subscription || null;
    } catch (error) {
      console.error('Error getting detailed subscription:', error);
      return null;
    }
  },

  /**
   * Create subscription payment intent for upgrading to premium
   */
  async createSubscriptionPaymentIntent(subscriptionTier) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for customer info
      const { data: profile } = await supabase?.from('user_profiles')?.select('full_name, email, phone')?.eq('id', user?.id)?.single();

      const orderData = {
        items: [{
          name: `${subscriptionTier === 'business' ? 'Commercial Operation' : 'Home Grower'} Subscription`,
          price: subscriptionTier === 'business' ? 14.99 : 4.99,
          quantity: 1
        }],
        total: subscriptionTier === 'business' ? 14.99 : 4.99,
        subtotal: subscriptionTier === 'business' ? 14.99 : 4.99,
        currency: 'USD'
      };

      const customerInfo = {
        userId: user?.id,
        firstName: profile?.full_name?.split(' ')?.[0] || 'User',
        lastName: profile?.full_name?.split(' ')?.slice(1)?.join(' ') || '',
        email: profile?.email || user?.email,
        phone: profile?.phone || '',
        billing: {
          address_line_1: '123 Main St', // Placeholder - should collect in checkout
          city: 'Denver',
          state: 'Colorado', 
          postal_code: '80202',
          country: 'US'
        }
      };

      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          orderData,
          customerInfo,
          subscriptionTier
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating subscription payment intent:', error);
      throw error;
    }
  }
};